import { createContext, useContext, useState } from "react";
import { AvrlBidCalc, BidRates } from "src/types/bid-rates";
import { RateData } from "src/types/rate-data";

type RateCalculatorState = {
    loading: boolean;
    setLoading: (loading: boolean) => void;
    loadingHistory: boolean;
    setLoadingHistory: (loading: boolean) => void;
    avrlBidRate: BidRates | null;
    setBidRates: (bidRates: BidRates) => void;
    clearBidRates: () => void;

    avrlBidCalc: AvrlBidCalc | null;
    setBidCalc: (bidCalc: AvrlBidCalc) => void;
    clearBidCalc: () => void;

    currentRateData: RateData | null;
    setCurrentRateData: (rateData: RateData) => void;

    currentFormData: RCForm;
    setCurrentFormData: (formData: RCForm) => void;
    clearCurrentRateData: () => void;
};

type RCForm = {
    LoadId: string;
    PickupTime: Date | null;
    DeliveryTime: Date | null;
    EquipmentType: string;
    Miles: number;

    OriginZip: string;
    DestinationZip: string;
    UPN: string;
    MilesFromAPI: boolean;
};

const rcFormDefault = {
    LoadId: "",
    PickupTime: null,
    DeliveryTime: null,
    EquipmentType: "dryvan",
    Miles: 0,

    OriginZip: "",
    DestinationZip: "",
    UPN: "",
    MilesFromAPI: true,
};

const DEFAULT_CONTEXT: RateCalculatorState = {
    loading: false,
    setLoading: () => {},
    loadingHistory: false,
    setLoadingHistory: () => {},
    avrlBidRate: null,
    setBidRates: () => {},
    clearBidRates: () => {},

    avrlBidCalc: null,
    setBidCalc: () => {},
    clearBidCalc: () => {},

    currentRateData: null,
    setCurrentRateData: () => {},
    clearCurrentRateData: () => {},

    currentFormData: rcFormDefault,
    setCurrentFormData: () => {},
};

const RateCalculatorContext =
    createContext<RateCalculatorState>(DEFAULT_CONTEXT);

// Provider
export const RateCalculatorProvider = ({
    children,
}: {
    children: React.ReactElement;
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
    const [bidRates, setBidRates] = useState<BidRates | null>(null);
    const [bidCalc, setBidCalc] = useState<AvrlBidCalc | null>(null);
    const [rateData, setRateData] = useState<RateData | null>(null);
    const [formData, setFormData] = useState<RCForm>(rcFormDefault);

    function handleClearBidRates() {
        setBidRates(null);
    }

    function handleClearBidCalc() {
        setBidCalc(null);
    }

    function handleClearCurrentRateData() {
        setRateData(null);
    }

    return (
        <RateCalculatorContext.Provider
            value={{
                loading: loading,
                setLoading: setLoading,
                loadingHistory: loadingHistory,
                setLoadingHistory: setLoadingHistory,
                avrlBidRate: bidRates,
                setBidRates: setBidRates,
                clearBidRates: handleClearBidRates,

                avrlBidCalc: bidCalc,
                setBidCalc: setBidCalc,
                clearBidCalc: handleClearBidCalc,

                currentRateData: rateData,
                setCurrentRateData: setRateData,
                clearCurrentRateData: handleClearCurrentRateData,

                currentFormData: formData,
                setCurrentFormData: setFormData,
            }}
        >
            {children}
        </RateCalculatorContext.Provider>
    );
};

// Hook
export const useRateCalculator = () => {
    const context = useContext(RateCalculatorContext);

    if (context === undefined) {
        throw new Error(
            "useRateCalculator must be used within a RateCalculatorProvider",
        );
    }
    return context;
};
